import { useState } from 'react'
import { Alert, Button, Card, Form, Input, Modal, Switch, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { fetchDeviceTypes, saveDeviceType, type DeviceType } from '@/features/subscriber/profileApi'

export function DeviceTypesPage() {
  const { t } = useTranslation()
  const [editing, setEditing] = useState<DeviceType | null>(null)
  const [formOpen, setFormOpen] = useState(false)

  const types = useQuery({
    queryKey: queryKeys.subscribers.deviceTypes(true),
    queryFn: () => fetchDeviceTypes(true),
  })

  const columns: ColumnsType<DeviceType> = [
    { key: 'name', title: t('deviceTypes.name'), dataIndex: 'name' },
    {
      key: 'active',
      title: t('deviceTypes.active'),
      width: 140,
      render: (_, row) =>
        row.active ? <Tag color="green">{t('listView.active')}</Tag> : <Tag>{t('listView.inactive')}</Tag>,
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'edit',
              label: t('common.edit'),
              permission: 'subscriber.devicetype.manage',
              onClick: () => {
                setEditing(row)
                setFormOpen(true)
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.deviceTypes')}
        description={t('pageIntro.deviceTypes')}
        extra={
          <Can permission="subscriber.devicetype.manage">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('deviceTypes.create')}
            </Button>
          </Can>
        }
      />
      {types.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(types.error, t)} />
      ) : null}
      <Card>
        <Table<DeviceType>
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={types.data ?? []}
          loading={types.isPending}
          pagination={false}
        />
      </Card>
      <DeviceTypeModal open={formOpen} deviceType={editing} onClose={() => setFormOpen(false)} />
    </>
  )
}

function DeviceTypeModal({
  open,
  deviceType,
  onClose,
}: {
  open: boolean
  deviceType: DeviceType | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [form] = Form.useForm<{ name: string; active: boolean }>()
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    submitOnValid(form, async (values) => {
      setSaving(true)
      setError(null)
      try {
        await saveDeviceType(deviceType?.id ?? null, { name: values.name.trim(), active: values.active })
        void queryClient.invalidateQueries({ queryKey: ['subscribers', 'deviceTypes'] })
        onClose()
      } catch (err) {
        setError(translateError(err, t))
      } finally {
        setSaving(false)
      }
    })
  }

  return (
    <Modal
      open={open}
      title={deviceType ? t('deviceTypes.edit') : t('deviceTypes.create')}
      onCancel={onClose}
      onOk={submit}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={saving}
      destroyOnHidden
      afterOpenChange={(visible) => {
        if (!visible) return
        setError(null)
        form.setFieldsValue({ name: deviceType?.name ?? '', active: deviceType?.active ?? true })
      }}
    >
      {error ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} /> : null}
      <Form form={form} layout="vertical" disabled={saving}>
        <Form.Item
          name="name"
          label={t('deviceTypes.name')}
          rules={[{ required: true, whitespace: true, max: 64, message: t('deviceTypes.nameRequired') }]}
        >
          <Input autoFocus autoComplete="off" />
        </Form.Item>
        <Form.Item name="active" label={t('deviceTypes.active')} valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
